import { Platform } from "@angular/cdk/platform";
import { DOCUMENT } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import {
  Component,
  HostBinding,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
} from "@angular/core";

import { Router } from "@angular/router";
import { ToastrModule } from "ngx-toastr";
import { Subject } from "rxjs";
import { AdminModule } from "./admin/admin.module";
import { HomeModule } from "./home/home.module";
import { InventoryModule } from "./inventory/inventory.module";
import { JobOrdersModule } from "./job-orders/job-orders.module";
import { AppLayout } from "./layout/components/app.layout";
import { LoginModule } from "./login/login.module";
import { OrderFacilitationModule } from "./order-facilitation/order-facilitation.module";
import { PurchasingModule } from "./purchasing/purchasing.module";
import { ScannerModule } from "./scanner/scanner.module";
import { SetupModule } from "./setup/setup.module";
import { HandheldModule } from "./handheld/handheld.module";
import { AuthService } from "./shared/auth.service";
import { PrimeNGUtilService } from "./shared/primeng.service";
import { SharedModule } from "./shared/shared.module";
import { ToastService } from "./shared/toast.service";
import { UtilFns } from "./shared/util-fns";

@Component({
  selector: "app",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: true,
  imports: [
    ToastrModule,

    // Layout
    AppLayout,

    // App modules
    SharedModule,

    // Uniformax modules
    LoginModule,
    HomeModule,
    InventoryModule,
    PurchasingModule,
    JobOrdersModule,
    ScannerModule,
    SetupModule,
    AdminModule,
    HandheldModule,
    OrderFacilitationModule,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  @HostBinding("@.disabled")
  public animationsDisabled = false; // disable all animations when true

  // navigation: any;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    @Inject(DOCUMENT) private document: any,

    private _authService: AuthService,
    private _toastService: ToastService,
    private _platform: Platform,
    private _router: Router,
    private _ngZone: NgZone,
    private _primeNGUtilService: PrimeNGUtilService
  ) {
    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      this.document.body.classList.add("is-mobile");
    }

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Disable animations when initial URL search string has 'qa' (e.g. '.../?qa')
    const p = new HttpParams({
      fromString: location.search && location.search.substring(1),
    });
    const qa = p.get("qa");
    this.disableAnimations(qa != null && qa !== "false");
    this._primeNGUtilService.setupNavigation();

    this.setupWindowRouting();
  }

  /** Expose window.navigateByUrl function to be called by Puppeteer */
  private setupWindowRouting() {
    const win = window as any;
    win.navigateByUrl = this.navigateByUrl.bind(this);
  }

  navigateByUrl(url: string) {
    url = UtilFns.trimBasePrefixFromPath(url);
    return this._ngZone.run(() => this._router.navigateByUrl(url));
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   *  Call in test to disable animations
   *
   * @param disable
   */
  disableAnimations(disable = true) {
    this.animationsDisabled = disable;
  }
}
